import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  formSection: {
    marginBottom: theme.spacing(2)
  },
  entriesList: {
    marginTop: theme.spacing(2),
    display: "grid",
    gap: theme.spacing(1)
  },
  entryCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`
  },
  entryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  linksRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  fullWidth: {
    width: "100%"
  }
}));

const ENTRY_TYPES = [
  { value: "service", label: "Servico" },
  { value: "work_order", label: "OS" },
  { value: "quotation", label: "Orcamento" },
  { value: "receipt", label: "Comprovante" }
];

const ENTRY_STATUS = [
  { value: "draft", label: "Rascunho" },
  { value: "sent", label: "Enviado" },
  { value: "approved", label: "Aprovado" },
  { value: "rejected", label: "Rejeitado" },
  { value: "paid", label: "Pago" },
  { value: "cancelled", label: "Cancelado" }
];

const getEntryTypeLabel = type =>
  ENTRY_TYPES.find(item => item.value === type)?.label || "Servico";

const formatAmount = (amount, currency) => {
  if (amount === null || typeof amount === "undefined" || amount === "") {
    return "";
  }

  const parsedAmount = Number(amount);
  if (!Number.isFinite(parsedAmount)) {
    return "";
  }

  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL"
  });

  return formatter.format(parsedAmount);
};

const buildInitialForm = ticket => ({
  type: "service",
  title: "",
  description: "",
  amount: "",
  currency: "BRL",
  quantity: 1,
  status: "draft",
  serviceDate: "",
  pdfUrl: "",
  externalLink: "",
  proofUrl: "",
  conversationDescription: ticket?.description || ""
});

const TicketServiceEntryModal = ({ open, onClose, ticket }) => {
  const classes = useStyles();
  const [entries, setEntries] = useState([]);
  const [formData, setFormData] = useState(buildInitialForm(ticket));
  const [loading, setLoading] = useState(false);
  const [uploadingKind, setUploadingKind] = useState("");
  const pdfUploadRef = useRef(null);
  const proofUploadRef = useRef(null);

  const ticketId = ticket?.id;

  useEffect(() => {
    if (!open || !ticketId) {
      return;
    }

    setFormData(buildInitialForm(ticket));

    const loadEntries = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}/service-entries`);
        setEntries(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    };

    loadEntries();
  }, [open, ticketId, ticket?.description]);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleCreateEntry = async () => {
    if (!ticketId) {
      return;
    }

    if (!formData.title.trim()) {
      return;
    }

    setLoading(true);
    try {
      await api.put(`/tickets/${ticketId}`, {
        description: formData.conversationDescription
      });

      const payload = {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        amount: formData.amount || null,
        currency: formData.currency,
        quantity: formData.quantity || 1,
        status: formData.status,
        serviceDate: formData.serviceDate || null,
        pdfUrl: formData.pdfUrl || null,
        externalLink: formData.externalLink || null,
        proofUrl: formData.proofUrl || null
      };

      const { data } = await api.post(`/tickets/${ticketId}/service-entries`, payload);

      setEntries(prevState => [data, ...prevState]);
      setFormData(prevState => ({
        ...prevState,
        title: "",
        description: "",
        amount: "",
        quantity: 1,
        serviceDate: "",
        pdfUrl: "",
        externalLink: "",
        proofUrl: ""
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async entryId => {
    if (!ticketId) {
      return;
    }

    try {
      await api.delete(`/tickets/${ticketId}/service-entries/${entryId}`);
      setEntries(prevState => prevState.filter(entry => entry.id !== entryId));
    } catch (err) {
      toastError(err);
    }
  };

  const handleUploadAttachment = async (file, kind) => {
    if (!ticketId || !file) {
      return;
    }

    setUploadingKind(kind);

    try {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("kind", kind);

      const { data } = await api.post(
        `/tickets/${ticketId}/service-entries/upload`,
        payload
      );

      setFormData(prevState => ({
        ...prevState,
        ...(kind === "proof"
          ? { proofUrl: data?.url || "" }
          : { pdfUrl: data?.url || "" })
      }));
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingKind("");
    }
  };

  const handleFileInputChange = (event, kind) => {
    const selectedFile = event.target.files?.[0];

    // Reset input value so selecting the same file again triggers onChange.
    event.target.value = "";

    if (!selectedFile) {
      return;
    }

    handleUploadAttachment(selectedFile, kind);
  };

  const sortedEntries = useMemo(() => {
    return [...entries].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [entries]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Lancamentos da conversa #{ticketId}</DialogTitle>
      <DialogContent dividers>
        <div className={classes.formSection}>
          <TextField
            className={classes.fullWidth}
            label="Descricao da conversa"
            name="conversationDescription"
            value={formData.conversationDescription}
            onChange={handleChange}
            multiline
            rows={3}
            variant="outlined"
            margin="dense"
          />
        </div>

        <Typography variant="subtitle1">Novo lancamento</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              variant="outlined"
              margin="dense"
              label="Tipo"
              name="type"
              value={formData.type}
              onChange={handleChange}
            >
              {ENTRY_TYPES.map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              variant="outlined"
              margin="dense"
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {ENTRY_STATUS.map(item => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Data do servico"
              name="serviceDate"
              type="datetime-local"
              value={formData.serviceDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Titulo"
              name="title"
              value={formData.title}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Descricao do lancamento"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Valor"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Moeda"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Quantidade"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="URL PDF (orcamento/OS)"
              name="pdfUrl"
              value={formData.pdfUrl}
              onChange={handleChange}
            />
            <input
              ref={pdfUploadRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={event => handleFileInputChange(event, "pdf")}
            />
            <Button
              onClick={() => pdfUploadRef.current?.click()}
              variant="outlined"
              size="small"
              disabled={uploadingKind === "pdf"}
              style={{ marginTop: 6 }}
            >
              {uploadingKind === "pdf"
                ? "Enviando arquivo..."
                : "Upload direto PDF/JPG (OS/Orcamento)"}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Link externo"
              name="externalLink"
              value={formData.externalLink}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              margin="dense"
              label="Link do comprovante"
              name="proofUrl"
              value={formData.proofUrl}
              onChange={handleChange}
            />
            <input
              ref={proofUploadRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={event => handleFileInputChange(event, "proof")}
            />
            <Button
              onClick={() => proofUploadRef.current?.click()}
              variant="outlined"
              size="small"
              disabled={uploadingKind === "proof"}
              style={{ marginTop: 6 }}
            >
              {uploadingKind === "proof"
                ? "Enviando arquivo..."
                : "Upload direto PDF/JPG (Comprovante)"}
            </Button>
          </Grid>
        </Grid>
        <Button
          color="primary"
          variant="contained"
          onClick={handleCreateEntry}
          disabled={loading || !formData.title.trim()}
          style={{ marginTop: 8 }}
        >
          Salvar lancamento
        </Button>

        <div className={classes.entriesList}>
          {sortedEntries.map(entry => (
            <Paper key={entry.id} className={classes.entryCard}>
              <div className={classes.entryHeader}>
                <Typography variant="subtitle2">
                  {getEntryTypeLabel(entry.type)} - {entry.title}
                </Typography>
                <IconButton size="small" onClick={() => handleDeleteEntry(entry.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>

              <Typography variant="body2" color="textSecondary">
                {entry.description || "Sem descricao"}
              </Typography>

              <Typography variant="caption" display="block">
                {entry.serviceDate
                  ? `Data: ${new Date(entry.serviceDate).toLocaleString("pt-BR")}`
                  : `Criado em: ${new Date(entry.createdAt).toLocaleString("pt-BR")}`}
              </Typography>

              <Typography variant="caption" display="block">
                {`Status: ${entry.status || "draft"}`}
              </Typography>

              {formatAmount(entry.amount, entry.currency) ? (
                <Typography variant="caption" display="block">
                  {`Valor: ${formatAmount(entry.amount, entry.currency)} | Qtd: ${
                    entry.quantity || 1
                  }`}
                </Typography>
              ) : null}

              <div className={classes.linksRow}>
                {entry.pdfUrl ? (
                  <Link href={entry.pdfUrl} target="_blank" rel="noopener noreferrer">
                    PDF
                  </Link>
                ) : null}
                {entry.externalLink ? (
                  <Link
                    href={entry.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Link
                  </Link>
                ) : null}
                {entry.proofUrl ? (
                  <Link href={entry.proofUrl} target="_blank" rel="noopener noreferrer">
                    Comprovante
                  </Link>
                ) : null}
              </div>
            </Paper>
          ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketServiceEntryModal;
